<template>
  <div class="phone-verify-form">
    <p v-if="hasMobile" class="phone-verify-form__mobile">
      {{ $t('phoneVerify.currentMobile') }}：
      <strong>{{ maskedMobile }}</strong>
    </p>
    <p v-else class="phone-verify-form__tip">{{ $t('phoneVerify.noMobile') }}</p>

    <el-form
      v-if="hasMobile"
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="0"
      class="phone-verify-form__el"
      @submit.prevent
    >
      <el-form-item prop="code">
        <el-input v-model="form.code" autocomplete="off" :placeholder="$t('phoneVerify.codePlaceholder')">
          <template #append>
            <el-button class="phone-verify-form__code-btn" :disabled="countdown > 0 || sending" @click="sendBoundCode">
              {{ codeButtonText }}
            </el-button>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" class="phone-verify-form__submit" :loading="submitting" @click="confirmBound">
          {{ $t('phoneVerify.submit') }}
        </el-button>
      </el-form-item>
    </el-form>

    <el-form
      v-else
      ref="bindFormRef"
      :model="bindForm"
      :rules="bindRules"
      label-width="0"
      class="phone-verify-form__el"
      @submit.prevent
    >
      <el-form-item prop="mobile">
        <el-input
          v-model="bindForm.mobile"
          autocomplete="off"
          :placeholder="$t('phoneVerify.bindMobile')"
          @change="resetCountdown"
        />
      </el-form-item>
      <el-form-item prop="code">
        <el-input v-model="bindForm.code" autocomplete="off" :placeholder="$t('phoneVerify.codePlaceholder')">
          <template #append>
            <el-button class="phone-verify-form__code-btn" :disabled="countdown > 0 || sending" @click="sendBindCode">
              {{ codeButtonText }}
            </el-button>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" class="phone-verify-form__submit" :loading="submitting" @click="confirmBind">
          {{ $t('phoneVerify.bindSubmit') }}
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { hidePhoneVerifyOverlay } from '@/utils/phoneVerify';

const props = defineProps({
  maskedMobile: {
    type: String,
    default: '',
  },
  hasMobile: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['success']);
const store = useStore();
const { t } = useI18n();

const formRef = ref(null);
const bindFormRef = ref(null);
const sending = ref(false);
const submitting = ref(false);
const countdown = ref(0);
let timer = null;

const form = reactive({ code: '' });
const bindForm = reactive({ mobile: '', code: '' });

const codeButtonText = computed(() => {
  if (countdown.value > 0) {
    return t('phoneVerify.retryAfter', { n: countdown.value });
  }
  return t('phoneVerify.getCode');
});

const rules = computed(() => ({
  code: [{ required: true, message: t('phoneVerify.enterCode'), trigger: 'blur' }],
}));
const bindRules = computed(() => ({
  mobile: [{ required: true, message: t('phoneVerify.enterMobile'), trigger: 'blur' }],
  code: [{ required: true, message: t('phoneVerify.enterCode'), trigger: 'blur' }],
}));

const startCountdown = () => {
  countdown.value = 60;
  if (timer) {
    clearInterval(timer);
  }
  timer = setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      clearInterval(timer);
      timer = null;
      countdown.value = 0;
    }
  }, 1000);
};

const resetCountdown = () => {
  countdown.value = 0;
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

const showError = (err) => {
  ElMessage({
    message: err?.message || err?.msg || '请求失败',
    grouping: true,
    type: 'error',
  });
};

const sendBoundCode = () => {
  if (sending.value || countdown.value > 0) return;
  sending.value = true;
  store.dispatch('user/PhoneSendCode', {}).then((res) => {
    ElMessage({
      message: res?.message || t('phoneVerify.getCode'),
      grouping: true,
      type: 'success',
    });
    startCountdown();
  }).catch((err) => {
    showError(err);
  }).finally(() => {
    sending.value = false;
  });
};

const confirmBound = () => {
  if (!formRef.value) return;
  formRef.value.validate((valid) => {
    if (!valid) return;
    submitting.value = true;
    store.dispatch('user/PhoneConfirm', { code: form.code }).then(() => {
      ElMessage({
        message: t('phoneVerify.success'),
        grouping: true,
        type: 'success',
      });
      store.dispatch('user/getPersonInfo', {}).finally(() => {
        hidePhoneVerifyOverlay();
        emit('success');
        submitting.value = false;
      });
    }).catch((err) => {
      showError(err);
      submitting.value = false;
    });
  });
};

const sendBindCode = () => {
  if (!bindForm.mobile) {
    ElMessage({
      message: t('phoneVerify.enterMobile'),
      grouping: true,
      type: 'warning',
    });
    return;
  }
  if (sending.value || countdown.value > 0) return;
  sending.value = true;
  store.dispatch('user/MobileCode', { mobile: bindForm.mobile }).then((res) => {
    ElMessage({
      message: res?.message || t('phoneVerify.getCode'),
      grouping: true,
      type: 'success',
    });
    startCountdown();
  }).catch((err) => {
    showError(err);
  }).finally(() => {
    sending.value = false;
  });
};

const confirmBind = () => {
  if (!bindFormRef.value) return;
  bindFormRef.value.validate((valid) => {
    if (!valid) return;
    submitting.value = true;
    store.dispatch('user/modifyPassword', {
      mobile: bindForm.mobile,
      code: bindForm.code,
    }).then(() => {
      ElMessage({
        message: t('phoneVerify.bindSuccess'),
        grouping: true,
        type: 'success',
      });
      store.dispatch('user/getPersonInfo', {}).then((res) => {
        if (!res?.phone_verify_required) {
          hidePhoneVerifyOverlay();
          emit('success');
        }
      }).finally(() => {
        submitting.value = false;
      });
    }).catch((err) => {
      showError(err);
      submitting.value = false;
    });
  });
};

onBeforeUnmount(() => {
  if (timer) {
    clearInterval(timer);
  }
});
</script>

<style scoped>
.phone-verify-form {
  width: 100%;
}
.phone-verify-form__mobile,
.phone-verify-form__tip {
  margin: 0 0 20px;
  color: #333;
  line-height: 1.6;
  font-size: 15px;
}
.phone-verify-form__submit {
  width: 100%;
}
:deep(.el-input-group__append) {
  min-width: 128px;
  padding: 0;
}
:deep(.phone-verify-form__code-btn) {
  width: 100%;
  margin: 0;
  padding: 0 10px;
}
</style>
